import { Injectable, OnDestroy } from '@angular/core';
import { Strings } from '@app/shared/constants';
import { RegionTypeEnum } from '@app/shared/enums';
import { ActiveKamUser } from '@app/shared/models/user';
import { MunicipalityService, MunicipalityView } from '@app/shared/services/api/municipality.service';
import { MunicipalityRegionHierarchy, RegionMunicipalitiesService } from '@app/shared/services/api/region-municipalities.service';
import { Region, RegionService, RegionType } from '@app/shared/services/api/regions.service';
import { UserService } from '@app/shared/services/api/user.service';
import { assign, flattenDeep, forEach } from 'lodash';
import { BehaviorSubject, combineLatest, forkJoin, merge, Observable, of, Subject } from 'rxjs';
import { filter, map, mergeMap, takeUntil, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RegionManagementDataService implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  private regionTypesStore: BehaviorSubject<RegionType[]> = new BehaviorSubject<RegionType[]>([]);
  private regionMunicipalityStore: BehaviorSubject<MunicipalityRegionHierarchy[]> = new BehaviorSubject<MunicipalityRegionHierarchy[]>([]);
  private regionsStore: BehaviorSubject<Region[]> = new BehaviorSubject<Region[]>([]);
  private municipalityStore: BehaviorSubject<MunicipalityView[]> = new BehaviorSubject<MunicipalityView[]>([]);
  private activeKamsStore: BehaviorSubject<ActiveKamUser[]> = new BehaviorSubject<ActiveKamUser[]>([]);

  public regionTypes$ = this.regionTypesStore.asObservable();
  public regionMunicipalities$ = this.regionMunicipalityStore.asObservable();
  public regions$ = this.regionsStore.asObservable();
  public municipalities$ = this.municipalityStore.asObservable();
  public activeKams$ = this.activeKamsStore.asObservable();

  constructor(
    private regionMunicipalities: RegionMunicipalitiesService,
    private region: RegionService,
    private municipality: MunicipalityService,
    private users: UserService
  ) {
    this.loadData();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public loadData(): void {
    forkJoin([this.getRegionTypes(), this.getRegionMunicipalities(), this.getRegions(), this.getMunicipalities(), this.users.getActiveKamUsers()])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([regionTypes, regionMunicipalities, regions, municipalities, activeKamUsers]) => {
        this.regionTypesStore.next(regionTypes);
        this.regionMunicipalityStore.next(regionMunicipalities);
        this.regionsStore.next(regions);
        this.municipalityStore.next(municipalities);
        this.activeKamsStore.next(activeKamUsers);
      });
  }

  public getRegionTypes = () => this.region.getRegionTypes();

  public getRegionMunicipalities = () => this.regionMunicipalities.getMunicipalityRegionMapping();

  public getRegions = () => this.region.getRegions();

  public getMunicipalities = () => this.municipality.getAll(true);

  public getRegionManagementData = () =>
    combineLatest([
      this.regionTypes$.pipe(filter((regionTypes: RegionType[]) => regionTypes.length > 0)),
      this.regionMunicipalities$.pipe(filter((regionMunicipalities: MunicipalityRegionHierarchy[]) => regionMunicipalities.length > 0)),
      this.regions$.pipe(filter((regions: Region[]) => regions.length > 0)),
      this.municipalities$.pipe(filter((municipalites: MunicipalityView[]) => municipalites.length > 0)),
      this.region.getById(Strings.poHoldingRegionid),
    ]);

  public addMunicipality = (municipality: MunicipalityView, municipalities: MunicipalityView[]) => {
    municipalities.push(municipality);
    return municipalities;
  };

  public getRegionMunicipalityById = (municipalityId: string) =>
    this.regionMunicipalities$.pipe(
      map((regionMunicipalities: MunicipalityRegionHierarchy[]) =>
        regionMunicipalities.find((regionMunicipality: MunicipalityRegionHierarchy) => regionMunicipality.municipalityId === municipalityId)
      )
    );

  public getMunicipality = (guid: string) =>
    this.municipalities$.pipe(map((municipalites: MunicipalityView[]) => municipalites.find((municipality: MunicipalityView) => municipality.guid === guid)));

  public getRegion = (guid: string) => this.regions$.pipe(map((regions: Region[]) => regions.find((region: Region) => region.guid === guid)));

  public reloadMunicipalities = () => this.getMunicipalities().pipe(tap((municipalites: MunicipalityView[]) => this.municipalityStore.next(municipalites)));

  public updateRegionMunicipalitiesRegionMapping = (region: Region) => {
    return this.getRegionType(region.regionTypeId).pipe(
      filter((regionType?: RegionType): regionType is RegionType => !!regionType),
      map((regionType: RegionType) => ({ region: region, type: regionType })),
      mergeMap((x) => this.mergeUpdateRegionMunicipallties(x.region, x.type))
    );
  };

  public getRegionType = (guid: string) =>
    this.regionTypes$.pipe(map((regionTypes: RegionType[]) => regionTypes.find((regionType) => regionType.guid === guid)));

  public updateRegion = (newRegion: Region) =>
    this.regions$.pipe(
      mergeMap(() => this.getRegion(newRegion.guid)),
      filter((region?: Region): region is Region => !!region),
      map((region: Region) => this.setRegionValue(region, newRegion)),
      mergeMap((region: Region) => this.updateRegionMunicipalitiesRegionMapping(region))
    );

  public addRegion = (region: Region) => {
    this.regionsStore.next([...this.regionsStore.getValue(), region]);
    return of(this.regionsStore.getValue());
  };

  public deleteRegion = (guid: string): Observable<Region[]> => {
    this.regionsStore.next(this.regionsStore.getValue().filter((region: Region) => region.guid !== guid));
    return of(this.regionsStore.getValue());
  };

  public addRegionMunicipality = (regionMunicipality: MunicipalityRegionHierarchy) => {
    this.regionMunicipalityStore.next([...this.regionMunicipalityStore.getValue(), regionMunicipality]);
  };

  public getRegionMunicipalityMappingByMunicipalityId = (municipalityId: string) =>
    this.regionMunicipalities.getMunicipalityRegionMappingByMunicipalityId(municipalityId).pipe(mergeMap((x) => x));

  public findRegionByGuid = (guid: string) => this.regions$.pipe(map((regions: Region[]) => regions.find((region: Region) => region.guid === guid)));

  public findRegionByName = (name: string) => this.regions$.pipe(map((regions: Region[]) => regions.find((region: Region) => region.name === name)));

  public findRegionNameWithParentRegionId = (name: string, parentRegionId: string) =>
    of({ name, parentRegionId }).pipe(
      mergeMap((x) => (x.parentRegionId ? this.findRegionByGuid(parentRegionId) : this.findRegionByName(x.name))),
      mergeMap((x) => (x ? this.findRDuplicateRegion(x, name) : of(null)))
    );

  private findRDuplicateRegion = (region: Region, name: string) =>
    of(region).pipe(
      mergeMap((region: Region) => this.regions$.pipe(map((regions: Region[]) => this.findRoot(regions, region)))),
      mergeMap((region: Region) => this.regions$.pipe(map((regions: Region[]) => [region, ...this.findChildren(region, regions)]))),
      map((regions: Region[]) => flattenDeep(regions)),
      map((regions: Region[]) => regions.find((region: Region) => region.name.trim() === name.trim()))
    );

  private findChildren = (region: Region, regions: Region[]) => {
    const children = regions.filter((r) => r.parentRegionId === region.guid);
    if (children.length > 0) {
      forEach(children, (child) => {
        this.findChildren(child, regions);
      });
      return children;
    }
    return children;
  };

  private mergeUpdateRegionMunicipallties = (region: Region, regionType: RegionType) =>
    merge(
      this.updateRegionMunicipalitiesAdminRegionName(region, regionType),
      this.updateRegionMunicipalitiesBackOfficeRegionName(region, regionType),
      this.updateRegionMunicipalitiesKamRegionName(region, regionType),
      this.updateRegionMunicipalitiesServiceRegionName(region, regionType),
      this.updateRegionMunicipalitiesCustomerServiceRegionName(region, regionType)
    );

  private setRegionValue = (region: Region, value: Region) => {
    region.name = value.name;
    return region;
  };

  private updateRegionMunicipalitiesAdminRegionName = (adminRegion: Region, regionType: RegionType) =>
    this.regionMunicipalities$.pipe(
      filter(() => regionType.code === RegionTypeEnum.adminAreaRegion),
      map((regionMunicipalities: MunicipalityRegionHierarchy[]) =>
        regionMunicipalities.find((regionMunicipality: MunicipalityRegionHierarchy) => regionMunicipality.adminRegionId?.some((id) => id === adminRegion.guid))
      ),
      map((regionMunicipality?: MunicipalityRegionHierarchy) => {
        if (!!regionMunicipality) {
          regionMunicipality.adminRegionName = [adminRegion.name];
        }
        return regionMunicipality;
      })
    );

  private updateRegionMunicipalitiesBackOfficeRegionName = (backOfficeRegion: Region, regionType: RegionType) =>
    this.regionMunicipalities$.pipe(
      filter(() => regionType.code === RegionTypeEnum.backOfficeRegion),
      map((regionMunicipalities: MunicipalityRegionHierarchy[]) =>
        regionMunicipalities.find((regionMunicipality: MunicipalityRegionHierarchy) => regionMunicipality.backOfficeRegionId === backOfficeRegion.guid)
      ),
      map((regionMunicipality?: MunicipalityRegionHierarchy) => {
        if (!!regionMunicipality) {
          regionMunicipality.backOfficeRegionName = backOfficeRegion.name;
        }
        return regionMunicipality;
      })
    );

  private updateRegionMunicipalitiesKamRegionName = (kamRegion: Region, regionType: RegionType) =>
    this.regionMunicipalities$.pipe(
      filter(() => regionType.code === RegionTypeEnum.kamRegion),
      map((regionMunicipalities: MunicipalityRegionHierarchy[]) =>
        regionMunicipalities.find((regionMunicipality: MunicipalityRegionHierarchy) => regionMunicipality.kamRegionId === kamRegion.guid)
      ),
      map((regionMunicipality?: MunicipalityRegionHierarchy) => {
        if (!!regionMunicipality) {
          regionMunicipality.kamRegionName = kamRegion.name;
        }
        return regionMunicipality;
      })
    );

  private updateRegionMunicipalitiesServiceRegionName = (servieRegion: Region, regionType: RegionType) =>
    this.regionMunicipalities$.pipe(
      filter(() => regionType.code === RegionTypeEnum.serviceRegion),
      map((regionMunicipalities: MunicipalityRegionHierarchy[]) =>
        regionMunicipalities.find((regionMunicipality: MunicipalityRegionHierarchy) => regionMunicipality.serviceRegionId === servieRegion.guid)
      ),
      map((regionMunicipality?: MunicipalityRegionHierarchy) => {
        if (!!regionMunicipality) {
          regionMunicipality.kamRegionName = servieRegion.name;
        }
        return regionMunicipality;
      })
    );

  private updateRegionMunicipalitiesCustomerServiceRegionName = (customerServieRegion: Region, regionType: RegionType) =>
    this.regionMunicipalities$.pipe(
      filter(() => regionType.code === RegionTypeEnum.customerServiceRegion),
      map((regionMunicipalities: MunicipalityRegionHierarchy[]) =>
        regionMunicipalities.find((regionMunicipality: MunicipalityRegionHierarchy) => regionMunicipality.serviceRegionId === customerServieRegion.guid)
      ),
      map((regionMunicipality?: MunicipalityRegionHierarchy) => {
        if (!!regionMunicipality) {
          regionMunicipality.kamRegionName = customerServieRegion.name;
        }
        return regionMunicipality;
      })
    );

  private findRoot = (list: any[], region: Region): Region =>
    !region.parentRegionId
      ? region
      : this.findRoot(
          list,
          list.find((x) => x.guid === region.parentRegionId)
        );

  updatePoHoldingRegionMunicipality(municipalityId: string, regionMunicipality: any, municipalityName?: string, municipalityKey?: string) {
    const mapping = this.regionMunicipalityStore.value.find((a) => a.municipalityId === municipalityId);
    if (mapping) {
      assign(mapping, regionMunicipality);
    } else {
      const obj: { [key: string]: any } = this.regionMunicipalityStore.value[0];
      Object.keys(obj).forEach((k) => (obj[k] = null));
      obj.poHoldingRegionId = regionMunicipality.poHoldingRegionId;
      obj.poHoldingRegionName = regionMunicipality.poHoldingRegionName;
      obj.municipalityId = municipalityId;
      obj.municipalityKey = municipalityKey;
      obj.municipalityName = municipalityName;
      assign(obj, regionMunicipality);
    }
  }
}
